export type AgentPrivileges = {
  isAdmin: boolean;
  isConcierge: boolean;
  ownEndToEnd: boolean;
  viewSubscribers: boolean;
  viewClaimedBusinesses: boolean;
  viewBilling: boolean;
  manageQueue: boolean;
};

export type AgentRecord = {
  id: string;
  userId?: string | null;
  name: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  zipcode?: string | null;
  isActive: boolean;
  source: "discovered" | "manual" | "current-user";
  privileges: AgentPrivileges;
};

export type AssignmentOverride = {
  ownerAgentId?: string | null;
  ownerAgentName?: string | null;
  status?: string | null;
  email?: string | null;
  phone?: string | null;
};

export type AgentApplicationRecord = {
  id: string;
  email?: string | null;
  username?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  zipcode?: string | null;
  serviceAreas?: string | null;
  specialties?: string | null;
  notes?: string | null;
  status?: string | null;
  reviewedAt?: string | null;
  reviewNotes?: string | null;
  approvedUserId?: string | null;
  createdAt?: string | null;
};

export const DEFAULT_PRIVILEGES: AgentPrivileges = {
  isAdmin: false,
  isConcierge: false,
  ownEndToEnd: true,
  viewSubscribers: true,
  viewClaimedBusinesses: true,
  viewBilling: true,
  manageQueue: true,
};

export function parseObject(raw: any): Record<string, any> {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(String(raw));
  } catch {
    return {};
  }
}

export function formatDate(value: any) {
  if (!value) return "-";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function normalizeText(value: any) {
  return String(value || "").trim();
}

function isPlaceholderAgentName(value: any) {
  const name = normalizeText(value);
  if (!name) return true;
  return /^agent\s+\d+$/i.test(name) || /^current user$/i.test(name);
}

function pickBetterAgentName(primary: any, fallback: any) {
  const primaryName = normalizeText(primary);
  const fallbackName = normalizeText(fallback);
  if (!primaryName) return fallbackName || "";
  if (isPlaceholderAgentName(primaryName) && fallbackName) return fallbackName;
  return primaryName;
}

export function hasResolvedAgentIdentity(agent: {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
}) {
  const name = normalizeText(agent?.name);
  const email = normalizeText(agent?.email);
  const phone = normalizeText(agent?.phone);
  if (email || phone) return true;
  return Boolean(name) && !isPlaceholderAgentName(name);
}

export function getAgentDisplayLabel(agent: {
  userId?: string | null;
  name?: string | null;
  email?: string | null;
}) {
  const name = normalizeText(agent?.name);
  const email = normalizeText(agent?.email);
  const userId = normalizeText(agent?.userId);
  if (name && !isPlaceholderAgentName(name)) return name;
  if (email) return email;
  if (userId) return `User ${userId}`;
  return name || "Assigned";
}

function getAgentKey(input: {
  userId?: string | null;
  email?: string | null;
  name?: string | null;
}) {
  const userId = normalizeText(input.userId);
  const email = normalizeText(input.email).toLowerCase();
  const name = normalizeText(input.name).toLowerCase();
  if (userId) return `user:${userId}`;
  if (email) return `email:${email}`;
  return `name:${name || "agent"}`;
}

export function mergeAgent(
  bucket: Record<string, AgentRecord>,
  next: Partial<AgentRecord> & {
    name?: string | null;
    userId?: string | null;
    email?: string | null;
    phone?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    zipcode?: string | null;
  },
) {
  const key = getAgentKey(next);
  const prev = bucket[key];
  bucket[key] = {
    id: prev?.id || key,
    userId: normalizeText(next.userId) || prev?.userId || null,
    email: normalizeText(next.email) || prev?.email || null,
    phone: normalizeText(next.phone) || prev?.phone || null,
    address: normalizeText(next.address) || prev?.address || null,
    city: normalizeText(next.city) || prev?.city || null,
    state: normalizeText(next.state) || prev?.state || null,
    country: normalizeText(next.country) || prev?.country || null,
    zipcode: normalizeText(next.zipcode) || prev?.zipcode || null,
    name: pickBetterAgentName(next.name, prev?.name) || "Unassigned agent",
    isActive:
      typeof (next as any).isActive === "boolean"
        ? Boolean((next as any).isActive)
        : prev?.isActive ?? true,
    source: (next.source as AgentRecord["source"]) || prev?.source || "discovered",
    privileges: prev?.privileges || DEFAULT_PRIVILEGES,
  };
}

export function getAgentFromLead(lead: any) {
  const extra = parseObject(lead?.extra);
  const concierge = parseObject(extra?.concierge);
  const agent = parseObject(
    lead?.ownerAgent ||
      lead?.assignedAgent ||
      extra?.agent ||
      concierge?.agent ||
      extra?.assignedAgent ||
      null,
  );
  const ownerAgentId =
    concierge?.ownerAgentId ??
    lead?.ownerAgentId ??
    lead?.assignedAgentId ??
    null;
  return {
    userId: ownerAgentId != null ? String(ownerAgentId) : null,
    name:
      normalizeText(agent?.name) ||
      normalizeText(agent?.username) ||
      normalizeText(lead?.ownerAgentName) ||
      normalizeText(lead?.assignedAgentName) ||
      normalizeText(concierge?.ownerAgentName) ||
      (ownerAgentId != null ? `Agent ${ownerAgentId}` : ""),
    email: normalizeText(agent?.email) || null,
    phone: normalizeText(agent?.phone) || null,
  };
}

export function getScreeningConcierge(group: any) {
  const seed = parseObject(group?.seedJson);
  const extra = parseObject(seed?.extra);
  const concierge = parseObject(extra?.concierge);
  const agent = parseObject(
    group?.ownerAgent || group?.assignedAgent || concierge?.agent || extra?.agent || null,
  );
  const ownerAgentId =
    concierge?.ownerAgentId ?? group?.ownerAgentId ?? group?.assignedAgentId ?? null;
  return {
    userId: ownerAgentId != null ? String(ownerAgentId) : null,
    name:
      normalizeText(agent?.name) ||
      normalizeText(agent?.username) ||
      normalizeText(group?.ownerAgentName) ||
      normalizeText(group?.assignedAgentName) ||
      normalizeText(concierge?.ownerAgentName) ||
      (ownerAgentId != null ? `Agent ${ownerAgentId}` : ""),
    email: normalizeText(agent?.email) || null,
    phone: normalizeText(agent?.phone) || null,
    status: normalizeText(concierge?.status) || "REQUESTED",
  };
}

export function findAgentRecord(
  agentMap: Record<string, AgentRecord>,
  input: { userId?: string | null; email?: string | null; name?: string | null },
) {
  const userId = normalizeText(input.userId);
  const email = normalizeText(input.email).toLowerCase();
  const name = normalizeText(input.name);
  if (userId && agentMap[`user:${userId}`]) return agentMap[`user:${userId}`];
  if (email && agentMap[`email:${email}`]) return agentMap[`email:${email}`];
  if (name && agentMap[`name:${name.toLowerCase()}`]) {
    return agentMap[`name:${name.toLowerCase()}`];
  }
  return null;
}

export function buildAgentDetails(
  agentMap: Record<string, AgentRecord>,
  input: {
    userId?: string | null;
    email?: string | null;
    phone?: string | null;
    name?: string | null;
  },
): AgentRecord | null {
  const resolved = findAgentRecord(agentMap, input);
  if (resolved) {
    const inputUserId = normalizeText(input.userId);
    const resolvedUserId = normalizeText(resolved.userId);
    const inputEmail = normalizeText(input.email).toLowerCase();
    const resolvedEmail = normalizeText(resolved.email).toLowerCase();
    const isSameResolvedIdentity =
      (inputUserId && resolvedUserId && inputUserId === resolvedUserId) ||
      (inputEmail && resolvedEmail && inputEmail === resolvedEmail);

    return {
      ...resolved,
      userId: inputUserId || resolved.userId || null,
      email: normalizeText(input.email) || resolved.email || null,
      phone: normalizeText(input.phone) || resolved.phone || null,
      name:
        (isSameResolvedIdentity
          ? pickBetterAgentName(resolved.name, input.name)
          : pickBetterAgentName(input.name, resolved.name)) || "Unassigned agent",
    };
  }

  const hasIdentity =
    normalizeText(input.userId) ||
    normalizeText(input.email) ||
    normalizeText(input.phone) ||
    normalizeText(input.name);
  if (!hasIdentity) return null;

  return {
    id: getAgentKey(input),
    userId: normalizeText(input.userId) || null,
    email: normalizeText(input.email) || null,
    phone: normalizeText(input.phone) || null,
    name: pickBetterAgentName(input.name, "") || "Unassigned agent",
    isActive: true,
    source: "discovered",
    privileges: { ...DEFAULT_PRIVILEGES },
  };
}

export function getConciergeStateFromLead(lead: any) {
  const extra = parseObject(lead?.extra);
  return parseObject(extra?.concierge);
}

export function getConciergeStateFromGroup(group: any) {
  const seed = parseObject(group?.seedJson);
  const extra = parseObject(seed?.extra);
  return parseObject(extra?.concierge);
}

export function getCaseHistory(concierge: any) {
  const assignments = Array.isArray(concierge?.assignments)
    ? concierge.assignments
    : [];
  const activity = Array.isArray(concierge?.activityLog)
    ? concierge.activityLog
    : [];
  return { assignments, activity };
}

function getLeadSourceMeta(raw: any) {
  const extra = parseObject(raw);
  const leadSource = parseObject(extra?.leadSource);
  const type = normalizeText(leadSource?.type).toUpperCase() || "PLATFORM_DIRECT";
  const sourceAgentId = normalizeText(leadSource?.sourceAgentId) || null;
  const sourceAgentName = normalizeText(leadSource?.sourceAgentName) || null;
  const attributionMethod =
    normalizeText(leadSource?.attributionMethod) || "PLATFORM_INTAKE";
  return { type, sourceAgentId, sourceAgentName, attributionMethod };
}

export function isAssignableAgentUser(user: any) {
  return user?.isAgent === true || user?.isDefaultConcierge === true;
}

export function isVisibleAgentTabUser(user: any) {
  return isAssignableAgentUser(user) || user?.isPlatformAdmin === true;
}

export function isAgentProfileComplete(agent: AgentRecord | null | undefined) {
  if (!agent) return false;
  return Boolean(
    normalizeText(agent.phone) &&
      normalizeText(agent.city) &&
      normalizeText(agent.state) &&
      normalizeText(agent.country) &&
      normalizeText(agent.zipcode),
  );
}

export function getAgentLocationLabel(agent: AgentRecord | null | undefined) {
  if (!agent) return "-";
  const city = normalizeText(agent.city);
  const state = normalizeText(agent.state);
  const zipcode = normalizeText(agent.zipcode);
  const country = normalizeText(agent.country);
  const first = [city, state].filter(Boolean).join(", ");
  const second = [zipcode, country].filter(Boolean).join(" • ");
  return [first, second].filter(Boolean).join(" • ") || "-";
}

export function getAttributionMeta(raw: any) {
  const seed = parseObject(raw?.seedJson);
  const rawExtra = parseObject(raw?.extra);
  const seedExtra = parseObject(seed?.extra);
  const summary = parseObject(
    raw?.attributionSummary ||
      seed?.attributionSummary ||
      rawExtra?.attributionSummary ||
      seedExtra?.attributionSummary,
  );
  const fromRawExtra = getLeadSourceMeta(rawExtra);
  const fromSeedExtra = getLeadSourceMeta(seedExtra);
  const fromRaw = getLeadSourceMeta(raw);
  return {
    type:
      normalizeText(raw?.leadSourceType || summary?.leadSourceType).toUpperCase() ||
      normalizeText(seed?.leadSourceType).toUpperCase() ||
      fromRawExtra.type ||
      fromSeedExtra.type ||
      fromRaw.type,
    sourceAgentId:
      normalizeText(raw?.sourceAgentId || summary?.sourceAgentId || seed?.sourceAgentId) ||
      fromRawExtra.sourceAgentId ||
      fromSeedExtra.sourceAgentId ||
      fromRaw.sourceAgentId,
    sourceAgentName:
      normalizeText(raw?.sourceAgentName || summary?.sourceAgentName || seed?.sourceAgentName) ||
      fromRawExtra.sourceAgentName ||
      fromSeedExtra.sourceAgentName ||
      fromRaw.sourceAgentName,
    intakeRoutingStatus:
      normalizeText(
        raw?.intakeRoutingStatus ||
          summary?.intakeRoutingStatus ||
          seed?.intakeRoutingStatus ||
          seedExtra?.intakeRoutingStatus,
      ).toUpperCase() ||
      "",
    routingMode:
      normalizeText(
        raw?.routingMode || summary?.routingMode || seed?.routingMode || seedExtra?.routingMode,
      ).toUpperCase() || "",
    assignedAgentId:
      normalizeText(
        raw?.assignedAgentId ||
          summary?.assignedAgentId ||
          seed?.assignedAgentId ||
          seedExtra?.assignedAgentId,
      ) || null,
    assignedAgentName:
      normalizeText(
        raw?.assignedAgentName ||
          summary?.assignedAgentName ||
          seed?.assignedAgentName ||
          seedExtra?.assignedAgentName,
      ) || null,
    commissionAgentId:
      normalizeText(
        raw?.commissionAgentId ||
          summary?.commissionAgentId ||
          seed?.commissionAgentId ||
          seedExtra?.commissionAgentId,
      ) || null,
    commissionAgentName:
      normalizeText(
        raw?.commissionAgentName ||
          summary?.commissionAgentName ||
          seed?.commissionAgentName ||
          seedExtra?.commissionAgentName,
      ) || null,
    assignmentOrigin:
      normalizeText(
        raw?.assignmentOrigin ||
          summary?.assignmentOrigin ||
          seed?.assignmentOrigin ||
          seedExtra?.assignmentOrigin,
      ).toUpperCase() || "",
  };
}

export function getRoutingLabel(args: {
  ownerAgentId?: string | null;
  assignmentCount?: number;
  intakeRoutingStatus?: string | null;
  routingMode?: string | null;
}) {
  const intakeRoutingStatus = normalizeText(args.intakeRoutingStatus).toUpperCase();
  const routingMode = normalizeText(args.routingMode).toUpperCase();
  if (intakeRoutingStatus === "ADMIN_REVIEW") return "Admin review";
  if (intakeRoutingStatus === "CLAIM_POOL_OPEN") return "Open claim pool";
  if (intakeRoutingStatus === "ASSIGNED" && routingMode === "ADMIN_ASSIGN") return "Assigned";
  if (intakeRoutingStatus === "ASSIGNED" && routingMode === "CLAIM_POOL") {
    return "Claimed from pool";
  }
  if (normalizeText(args.ownerAgentId)) return "Assigned";
  if ((args.assignmentCount || 0) > 0) return "Reopened to claim pool";
  return "Admin review / claim pool";
}
